import React, { useState, useEffect } from "react";
import { Typography, Grid, Card, Avatar, Box, Button } from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate,useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { api, needsPostTripClosurePrompt, completeBookingWithoutTip, firebase } from "common";
import { onValue } from "firebase/database";
import DispatchTrackingCard from "../components/DispatchTrackingCard";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";
import moment from "moment/min/moment-with-locales";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { makeStyles } from "@mui/styles";
import { SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions"
import GoBackButton from "components/GoBackButton";

const getBookingStopsForDisplay = (booking) => {
  if (!booking) {
    return [];
  }
  if (Array.isArray(booking.stops) && booking.stops.length > 0) {
    return booking.stops;
  }
  if (Array.isArray(booking.waypoints) && booking.waypoints.length > 0) {
    return booking.waypoints;
  }
  return [];
};

const formatCurrencyAmount = (amount, settings) => {
  const formatted = parseFloat(amount || 0).toFixed(settings.decimal);
  return settings.swipe_symbol
    ? `${formatted} ${settings.symbol}`
    : `${settings.symbol} ${formatted}`;
};

const buildStopoverPaymentField = (booking, settings, t) => {
  const stopsCount = getBookingStopsForDisplay(booking).length;
  if (stopsCount === 0) {
    return null;
  }

  const stopoverFeesTotal = parseFloat(booking.stopover_fees_total || 0);
  if (!stopoverFeesTotal || stopoverFeesTotal <= 0) {
    return null;
  }

  const stopoverFeePerStop =
    parseFloat(booking.stopover_fee_per_stop || 0) ||
    stopoverFeesTotal / stopsCount;

  return {
    key: "stopover_fees_total",
    label: t("stopovers_fare", {
      count: stopsCount,
      fee: parseFloat(stopoverFeePerStop).toFixed(settings.decimal),
    }),
    value: formatCurrencyAmount(stopoverFeesTotal, settings),
  };
};

const buildIntermediateStopFields = (booking, t) => {
  return getBookingStopsForDisplay(booking).map((stop, index) => {
    const address =
      stop?.add ||
      stop?.description ||
      stop?.address ||
      "";
    const statusSuffix =
      stop?.status === "COMPLETED"
        ? ` (${t("COMPLETE") || "Complete"})`
        : stop?.status === "ARRIVED"
          ? ` (${t("stopover_arrival_title") || "At stop"})`
          : "";
    return {
      key: `stop_address_${index}`,
      label: t("stopover_point_n", { n: index + 1 }),
      value: address ? `${address}${statusSuffix}` : "",
      typographyStyleAddress: { color: "#757575" },
    };
  });
};

const buildRideInformationFields = (data, settings, t) => {
  if (!data) {
    return [];
  }

  const pickupField = {
    key: "pickup_address",
    label: t("pickup_address"),
    value: data?.pickupAddress,
    addressGridStyle: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "row",
      minHeight: 60,
      marginBottom: 20,
    },
    typographyStyleAddress: { color: colors.GREEN },
  };

  const dropField = {
    key: "drop_address",
    label: t("drop_address"),
    value: data?.dropAddress,
    typographyStyleAddress: { color: colors.RED },
  };

  return [
    { key: "booking_id", label: t("booking_id"), value: data.id },
    { key: "booking_ref", label: t("booking_ref"), value: data.reference },
    {
      key: "booking_status_web",
      label: t("booking_status_web"),
      value:
        settings && settings.prepaid && data?.status === "PAYMENT_PENDING"
          ? t("PREPAID_INTERRUPTED")
          : t(data.status),
      style: {
        backgroundColor:
          data?.status === "CANCELLED"
            ? "#CC1900"
            : data?.status === "COMPLETE"
              ? "#0A8A02"
              : "#D4B73A",
        color: colors.WHITE,
        fontWeight: "bold",
        borderRadius: "10px",
        textAlign: "center",
        padding: 3,
      },
    },
    {
      key: "trip_start_date",
      label: t("trip_start_date"),
      value: data?.tripdate ? moment(data?.tripdate).format("lll") : "",
    },
    { key: "trip_start_time", label: t("trip_start_time"), value: data?.trip_start_time },
    { key: "trip_end_time", label: t("trip_end_time"), value: data?.trip_end_time },
    {
      key: "parcel_type_web",
      label: t("parcel_type_web"),
      value: data.parcelTypeSelected
        ? data.parcelTypeSelected.description +
          " (" +
          data.parcelTypeSelected.amount +
          ")"
        : "",
    },
    {
      key: "parcel_option",
      label: t("parcel_option"),
      value: data.optionSelected
        ? data.optionSelected.description +
          " (" +
          data.optionSelected.amount +
          ")"
        : "",
    },
    { key: "deliveryPerson", label: t("deliveryPerson"), value: data?.deliveryPerson },
    {
      key: "deliveryPersonPhone",
      label: t("deliveryPersonPhone"),
      value: data.deliveryPersonPhone
        ? settings.AllowCriticalEditsAdmin
          ? data.deliveryPersonPhone
          : t("hidden_demo")
        : "",
    },
    {
      key: "take_pickup_image_web",
      label: t("take_pickup_image_web"),
      value: data.pickup_image ? data.pickup_image : "",
      type: "image",
      alt: "pickup_image",
    },
    {
      key: "take_deliver_image_web",
      label: t("take_deliver_image_web"),
      value: data.deliver_image ? data.deliver_image : "",
      type: "image",
      alt: "deliver_image",
    },
    { key: "tripInstructions", label: t("tripInstructions"), value: data?.tripInstructions },
    { key: "cancellation_reason", label: t("cancellation_reason"), value: data?.reason },
    { key: "otp", label: t("otp"), value: data?.otp ? t("true") : t("false") },
    { key: "total_time", label: t("total_time"), value: data?.total_trip_time },
    {
      key: "distance_web",
      label: t("distance_web"),
      value: data.distance
        ? settings.convert_to_mile
          ? data?.distance + t("mile")
          : data?.distance + " " + t("km")
        : "",
    },
    { key: "pickUpInstructions_web", label: t("pickUpInstructions_web"), value: data?.pickUpInstructions },
    { key: "deliveryInstructions", label: t("deliveryInstructions"), value: data?.deliveryInstructions },
    { key: "otherPerson", label: t("otherPerson"), value: data?.otherPerson },
    { key: "otherPersonPhone", label: t("otherPersonPhone"), value: data?.otherPersonPhone },
    { key: "feedback", label: t("feedback"), value: data?.feedback },
    pickupField,
    ...buildIntermediateStopFields(data, t),
    dropField,
  ];
};

const buildPaymentInfoFields = (data, settings, t) => {
  if (!data) {
    return [];
  }

  const stopoverField = buildStopoverPaymentField(data, settings, t);

  return [
    {
      key: "trip_cost",
      label: t("trip_cost"),
      value: formatCurrencyAmount(data.trip_cost, settings),
    },
    ...(stopoverField ? [stopoverField] : []),
    {
      key: "Customer_paid",
      label: t("Customer_paid"),
      value: data?.customer_paid
        ? formatCurrencyAmount(data.customer_paid, settings)
        : "",
    },
    {
      key: "discount_ammount",
      label: t("discount_ammount"),
      value:
        data?.discount != null && data?.discount !== ""
          ? formatCurrencyAmount(data.discount, settings)
          : "",
    },
    {
      key: "driver_share",
      label: t("driver_share"),
      value: formatCurrencyAmount(data.driver_share, settings),
    },
    {
      key: "fleet_admin_comission",
      label: t("fleet_admin_comission"),
      value:
        data?.fleetCommission && parseFloat(data.fleetCommission) > 0
          ? formatCurrencyAmount(data.fleetCommission, settings)
          : "",
    },
    {
      key: "convenience_fee",
      label: t("convenience_fee"),
      value: formatCurrencyAmount(data.convenience_fees, settings),
    },
    {
      key: "tipamount",
      label: t("tipamount"),
      value: data?.tipamount
        ? formatCurrencyAmount(data.tipamount, settings)
        : "",
    },
    {
      key: "cancellationFee",
      label: t("cancellationFee"),
      value: data?.cancellationFee
        ? formatCurrencyAmount(data.cancellationFee, settings)
        : "",
    },
    {
      key: "payment_gateway",
      label: t("payment_gateway"),
      value: data?.gateway,
    },
    {
      key: "payment_mode_web",
      label: t("payment_mode_web"),
      value: data?.payment_mode ? data.payment_mode : "",
    },
    {
      key: "cash_payment_amount",
      label: t("cash_payment_amount"),
      value: data?.cashPaymentAmount
        ? formatCurrencyAmount(data.cashPaymentAmount, settings)
        : "",
    },
    {
      key: "card_payment_amount",
      label: t("card_payment_amount"),
      value: data?.cardPaymentAmount
        ? formatCurrencyAmount(data.cardPaymentAmount, settings)
        : "",
    },
    {
      key: "wallet_payment_amount",
      label: t("wallet_payment_amount"),
      value: data?.usedWalletMoney
        ? formatCurrencyAmount(data.usedWalletMoney, settings)
        : "",
    },
    {
      key: "payable_ammount",
      label: t("payable_ammount"),
      value: data?.payableAmount
        ? formatCurrencyAmount(data.payableAmount, settings)
        : "",
    },
  ];
};

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: "10px",
    backgroundColor: colors.WHITE,
    minHeight: 100,
    marginTop: 5,
    marginBottom: 20,
    boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
  },
  txt: {
    padding: 10,
    fontWeight: "bold",
    minHeight: 60,
    backgroundColor: SECONDORY_COLOR,
    color: colors.BLACK,
    boxShadow: 3,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

function BookingDetails() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const { updateBooking, cancelBooking, fetchUsersOnce } = api;
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const settings = useSelector((state) => state.settingsdata.settings);
  const classes = useStyles();
  const role = useSelector(state => state.auth.profile.usertype);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const { state } = useLocation();
  const [dispatchLogEntries, setDispatchLogEntries] = useState([]);

  const canMarkTripComplete =
    (role === "admin" || role === "fleetadmin") &&
    data &&
    needsPostTripClosurePrompt(data);

  const canCancelPaymentPending =
    (role === "admin" || role === "fleetadmin") &&
    data &&
    data.status === "PAYMENT_PENDING";

  const handleMarkTripComplete = () => {
    if (!data || !window.confirm(t("mark_booking_complete_confirm"))) {
      return;
    }
    setCompleting(true);
    const payload = completeBookingWithoutTip(dispatch, updateBooking, data, {
      rating: typeof data.rating === "number" ? data.rating : 0,
    });
    setData(payload);
    setCompleting(false);
    window.alert(t("mark_booking_complete_done"));
  };

  const handleCancelPaymentPending = () => {
    if (!data || !window.confirm(t("cancel_payment_pending_confirm"))) {
      return;
    }
    const reason = t("cancelled_payment_pending_admin");
    setCancelling(true);
    dispatch(
      cancelBooking({
        reason,
        booking: data,
        cancelledBy: role,
      }),
    );
    setData({
      ...data,
      status: "CANCELLED",
      reason,
      cancelledBy: role,
    });
    setCancelling(false);
    window.alert(t("cancel_payment_pending_done"));
  };
  
  useEffect(() => {
    if ((role === "admin" || role === "fleetadmin") && !staticusers) {
      dispatch(fetchUsersOnce());
    }
  }, [dispatch, fetchUsersOnce, role, staticusers]);

  useEffect(() => {
    if (bookinglistdata.bookings) {
      const booking = bookinglistdata.bookings.filter(
        (item) => item.id === id.toString()
      )[0];

      if (booking) {
        setData(booking);
      } else {
        navigate("/404");
        setData([]);
      }
    }
  }, [bookinglistdata.bookings, id, navigate]);

  useEffect(() => {
    if (!id) {
      return undefined;
    }
    const { dispatchLogRef } = firebase;
    const logRef = dispatchLogRef(id);
    const unsubscribe = onValue(logRef, (snapshot) => {
      const val = snapshot.val();
      if (!val) {
        setDispatchLogEntries([]);
        return;
      }
      const entries = Object.keys(val).map((key) => ({
        id: key,
        ...val[key],
      }));
      setDispatchLogEntries(entries);
    });
    return () => unsubscribe();
  }, [id]);

  const renderGridItem = (item, isRTL) => {
    return (
      <Grid
        key={item.key}
        container
        spacing={1}
        sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
        style={{
          justifyContent: "center",
          alignItems: "center",
          minHeight: 60,
          ...item.addressGridStyle
        }}
      >
        <Grid item xs={4}>
          <Typography
            style={{
              fontSize: 16,
              padding: 2,
              textAlign: isRTL === "rtl" ? "right" : "left",
              fontFamily:FONT_FAMILY,
              ...item.typographyStyleAddress
            }}
          >
            {item.label}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography
            style={{
              fontSize: 18,
              padding: 2,
              letterSpacing: -1,
              textAlign: "center",
              fontFamily:FONT_FAMILY,
            }}
          >
            -----
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography
            style={{
              fontSize: 16,
              padding: 2,
              textAlign: isRTL === "rtl" ? "left" : "right",
              wordBreak: 'break-word',
              fontFamily:FONT_FAMILY,
              ...item.style,
            }}
          >
            {item.value}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderTypography = (text) => {
    return (
      <Typography
      className={classes.txt}
      style={{
        borderBottomRightRadius: isRTL ? "90px" : "",
        borderBottomLeftRadius: isRTL ? "" : "90px",
        fontFamily:FONT_FAMILY,
      }}
      variant="h5"
      >
        {text}
      </Typography>
    );
  };

  return (
    <>
         <GoBackButton isRTL={isRTL}  onClick={() => { navigate('/bookings', { state: { pageNo: state?.pageNo }} ) }} />
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Grid item>
              <Card className={classes.card}> 
              {renderTypography(t("ride_information"))}
                <Grid container direction="column" style={{ padding: 10 }}>
                  {data &&
                    buildRideInformationFields(data, settings, t).map((item) =>
                      item.type === "image" && item.value ? (
                        <Grid
                          key={item.key}
                          container
                          spacing={1}
                          sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
                          style={{
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 60,
                          }}
                        >
                          <Grid item xs={4}>
                            <Typography
                              style={{
                                fontSize: 16,
                                padding: 2,
                                textAlign: isRTL === "rtl" ? "right" : "left",
                                fontFamily:FONT_FAMILY,
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography
                              style={{
                                fontSize: 18,
                                padding: 2,
                                letterSpacing: -1,
                                textAlign: "center",
                                fontFamily:FONT_FAMILY,
                              }}
                            >
                              -----
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <img
                              src={item.value}
                              alt={item.alt}
                              style={{
                                width: 120,
                                height: 90,
                              }}
                            />
                          </Grid>
                        </Grid>
                      ) :
                        item.value ?renderGridItem(item, isRTL) : null
                    )}
                  {canMarkTripComplete || canCancelPaymentPending ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        mt: 2,
                        mb: 1,
                      }}
                    >
                      {canMarkTripComplete ? (
                        <Button
                          variant="contained"
                          color="success"
                          disabled={completing || cancelling}
                          onClick={handleMarkTripComplete}
                          sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}
                        >
                          {t("mark_booking_complete_web")}
                        </Button>
                      ) : null}
                      {canCancelPaymentPending ? (
                        <Button
                          variant="contained"
                          color="error"
                          disabled={completing || cancelling}
                          onClick={handleCancelPaymentPending}
                          sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}
                        >
                          {t("cancel_payment_pending_web")}
                        </Button>
                      ) : null}
                    </Box>
                  ) : null}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(t("payment_info"))}
                <Grid container direction="column" style={{ paddingRight: 15, paddingLeft: 15, paddingBottom: 15 }}>
                  {data &&
                    buildPaymentInfoFields(data, settings, t).map((item) =>
                      item.value ? renderGridItem(item, isRTL) : null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(t("driver_info"))}
                <Grid container direction="column" style={{ padding: 15 }}>
                  <Grid
                    item
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {data?.driver_image ? (
                      <Avatar
                        alt="driver profile image"
                        src={data?.driver_image}
                        style={{ width: 100, height: 100, objectFit: "cover" }}
                      />
                    ) : (
                      <AccountCircleIcon sx={{ width: 100, height: 100 }} />
                    )}
                  </Grid>
                  {data &&
                    [
                      { key: "driver_id", label: t("driver_id"), value: data?.driver ? data.driver : "" },
                      { key: "driver_name", label: t("driver_name"), value: data?.driver_name ? data.driver_name : "" },
                      {
                        key: "driver_contact", label: t("driver_contact"), value: data?.driver_contact ? (settings.AllowCriticalEditsAdmin
                          ? data?.driver_contact
                          : t("hidden_demo")) : ""
                      },
                      {
                        key: "driver_email", label: t("driver_email"), value: data?.driver_email ? (settings.AllowCriticalEditsAdmin
                          ? data?.driver_email
                          : t("hidden_demo")) : ""
                      },
                      { key: "car_type", label: t("car_type"), value: data?.carType ? data.carType : "" },
                      { key: "vehicle_no", label: t("vehicle_no"), value: data?.vehicle_number ? data.vehicle_number : "" },
                      { key: "fleetadmins", label: t("fleetadmins"), value: data?.fleetadmin && role === 'admin' ? data.fleetadmin : "" },
                      { key: "device_id", label: t("device_id"), value: data?.driverDeviceId ? data.driverDeviceId : "" },

                    ].map((item) =>
                      item.value ? renderGridItem(item, isRTL): null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(t("customer_info"))}
                <Grid container direction="column" style={{ padding: 15 }}>
                  <Grid
                    item
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {data?.customer_image ? (
                      <Avatar
                        alt="customer profile image"
                        src={data?.customer_image}
                        style={{ width: 100, height: 100, objectFit: "cover" }}
                      />
                    ) : (
                      <AccountCircleIcon sx={{ width: 100, height: 100 }} />
                    )}
                  </Grid>
                  {data &&
                    [
                      { key: "customer_id", label: t("customer_id"), value: data?.customer ? data.customer : "" },
                      { key: "customer_name", label: t("customer_name"), value: data?.customer_name ? data.customer_name : "" },
                      {
                        key: "customer_contact", label: t("customer_contact"), value: data?.customer_contact ? (settings.AllowCriticalEditsAdmin
                          ? data?.customer_contact
                          : t("hidden_demo")) : ""
                      },
                      {
                        key: "customer_email", label: t("customer_email"), value: data?.customer_email ? (settings.AllowCriticalEditsAdmin
                          ? data?.customer_email
                          : t("hidden_demo")) : ""
                      },

                    ].map((item) =>
                      item.value ? renderGridItem(item, isRTL): null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          {(role === "admin" || role === "fleetadmin") && (
            <Grid item xs={12}>
              <DispatchTrackingCard
                logEntries={dispatchLogEntries}
                booking={data}
                users={staticusers || []}
                cardClassName={classes.card}
                titleClassName={classes.txt}
              />
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
}

export default BookingDetails;
